import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

export const sanityConfig = {
  projectId: 'qsztwno5',
  dataset: 'production',
  apiVersion: '2024-01-01',
  useCdn: false, // `false` para que los artículos aparezcan inmediatamente al publicarse
};

export const sanityClient = createClient(sanityConfig);

const builder = imageUrlBuilder(sanityClient);

export function urlFor(source: any) {
  return builder.image(source);
}

// Interfaces TypeScript para el Blog
export interface SanityCategory {
  _id: string;
  title: string;
  slug: { current: string };
  description?: string;
}

export interface SanityAuthor {
  _id: string;
  name: string;
  role?: string;
  image?: any;
  bio?: any;
}

export interface SanityPost {
  _id: string;
  title: string;
  slug: { current: string };
  language?: 'es' | 'en' | 'pt' | 'fr' | 'it' | 'de';
  isAutoTranslated?: boolean;
  translationGroupId?: string;
  sourceLanguage?: string;
  publishedAt: string;
  author?: SanityAuthor;
  mainImage?: any;
  categories?: SanityCategory[];
  excerpt?: string;
  body?: any;
  content?: any;
  readTimeMinutes?: number;
  // Campos SEO
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

/**
 * Consulta para obtener los artículos más recientes
 */
export async function getLatestPosts(limit = 6, lang = 'es'): Promise<SanityPost[]> {
  try {
    const query = `*[_type == "post" && !(_id in path("drafts.**")) && (language == $lang || (!defined(language) && $lang == 'es'))] | order(publishedAt desc)[0...$limit] {
      _id,
      title,
      slug,
      language,
      publishedAt,
      excerpt,
      mainImage,
      categories[]->{ _id, title, slug },
      author->{ _id, name, role, image },
      readTimeMinutes
    }`;
    const posts = await sanityClient.fetch<SanityPost[]>(query, { limit, lang });
    if (posts && posts.length > 0) return posts;

    // Fallback a español si no hay en el idioma seleccionado
    if (lang !== 'es') {
      return await sanityClient.fetch<SanityPost[]>(query, { limit, lang: 'es' });
    }
    return [];
  } catch (error) {
    console.error('Error fetching latest posts from Sanity:', error);
    return [];
  }
}

/**
 * Consulta para obtener un artículo por su slug
 */
export async function getPostBySlug(slug: string): Promise<SanityPost | null> {
  try {
    const query = `*[_type == "post" && slug.current == $slug][0] {
      _id,
      title,
      slug,
      language,
      publishedAt,
      excerpt,
      mainImage,
      body,
      categories[]->{ _id, title, slug },
      author->{ _id, name, role, image, bio },
      readTimeMinutes,
      seoTitle,
      seoDescription,
      seoKeywords
    }`;
    return await sanityClient.fetch(query, { slug });
  } catch (error) {
    console.error('Error fetching post by slug from Sanity:', error);
    return null;
  }
}
